// Summary.js
import React from 'react';
import '../FishFinderBuilder.css';
import FeedbackSection from './FeedbackSection';
import { findProductByName, findProductById } from '../data/productData';
import { getDealLinkById, getDealLinkByName } from '../data/dealLinks';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Summary = ({
  selectedScreen,
  selectedLivescope,
  selectedOpenWater,
  selectedIceFisher,
  battery,
  shuttle,
  pole,
  goBack,
  restart
}) => {
  // Add comprehensive debugging logs
  console.log("Summary component received props:", { 
    selectedScreen,
    selectedLivescope,
    selectedOpenWater,
    selectedIceFisher,
    battery,
    shuttle,
    pole,
  });

  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the latest product data for each selected product
  const latestScreen = selectedScreen ? findProductByName('screen', selectedScreen.name) || selectedScreen : null;
  const latestLivescope = selectedLivescope ? findProductByName('transducer', selectedLivescope.name) || selectedLivescope : null;
  const latestOpenWater = selectedOpenWater ? findProductByName('transducer', selectedOpenWater.name) || selectedOpenWater : null;
  const latestIceFisher = selectedIceFisher ? findProductByName('transducer', selectedIceFisher.name) || selectedIceFisher : null;
  const latestBattery = battery ? findProductByName('battery', battery.name) || battery : null;
  const latestShuttle = shuttle ? findProductByName('shuttle', shuttle.name) || shuttle : null;
  const latestPole = pole ? findProductByName('pole', pole.name) || pole : null;

  // Calculate total MSRP including all selected transducers
  const totalMSRP =
    (latestScreen?.msrp || 0) +
    (latestLivescope?.msrp || 0) +
    (latestOpenWater?.msrp || 0) +
    (latestIceFisher?.msrp || 0) +
    (latestBattery?.msrp || 0) +
    (latestShuttle?.msrp || 0) +
    (latestPole?.msrp || 0);

  // Format price with commas and 2 decimal places
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Navigation handlers
  const handleBack = () => {
    if (goBack) {
      goBack();
    } else if (navigate) {
      navigate(-1);
    }
  };

  const handleReset = () => {
    if (restart) {
      restart();
    } else if (navigate) {
      navigate('/compare-screens', { replace: true });
    }
  };

  // Helper function to render a transducer card
  const renderTransducerCard = (transducer) => {
    if (!transducer) return null;
    return (
      <div key={transducer.id} className="option-row summary-card">
        <div className="product-image-container">
          <img 
            src={transducer.image} 
            alt={transducer.name}
            className="product-image"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = transducer.fallbackImage || `https://dummyimage.com/150x100/cccccc/000000&text=Transducer`;
            }}
          />
        </div>
        <div className="option-details">
          <h3>{transducer.name}</h3>
          <p className="price-tag">MSRP: ${formatPrice(transducer.msrp)}</p>
          <div className="summary-actions">
            <a 
              href={transducer.productLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="deal-link"
            >
              Find the Best Deal
            </a>
            <button onClick={handleBack} className="change-btn">
              <span className="change-btn-text">Change</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="screen-selection-container">
      <div className="bundle-header-section">
        <h2>Your Fish Finder Bundle</h2>
        <div className="price-banner">
          <p className="bundle-msrp">
            Bundle MSRP: <span className="price-value">${formatPrice(totalMSRP)}</span>
          </p>
          <p className="msrp-note">Retail price - find discounts with our deal links</p>
        </div>
      </div>

      <div className="summary-grid">
        {/* Screen Card */}
        {latestScreen && (
          <div className="option-row summary-card">
            <div className="product-image-container">
              <img 
                src={latestScreen.image} 
                alt={latestScreen.name}
                className="product-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = latestScreen.fallbackImage || `https://dummyimage.com/150x100/cccccc/000000&text=Screen`;
                }}
              />
            </div>
            <div className="option-details">
              <h3>{latestScreen.name}</h3>
              <p className="price-tag">MSRP: ${formatPrice(latestScreen.msrp)}</p>
              <div className="summary-actions">
                <a 
                  href={latestScreen.productLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="deal-link"
                >
                  Find the Best Deal
                </a>
                <button onClick={handleBack} className="change-btn">
                  <span className="change-btn-text">Change</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transducer Cards - render all selected transducers */}
        {latestLivescope && renderTransducerCard(latestLivescope)}
        {latestOpenWater && renderTransducerCard(latestOpenWater)}
        {latestIceFisher && renderTransducerCard(latestIceFisher)}

        {/* Battery Card */}
        {latestBattery && (
          <div className="option-row summary-card">
            <div className="product-image-container">
              <img 
                src={latestBattery.image} 
                alt={latestBattery.name}
                className="product-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = latestBattery.fallbackImage || `https://dummyimage.com/150x100/cccccc/000000&text=Battery`;
                }}
              />
            </div>
            <div className="option-details">
              <h3>{latestBattery.name}</h3>
              <p className="price-tag">MSRP: ${formatPrice(latestBattery.msrp)}</p>
              {latestBattery.dealInfo && (
                <p className="exclusive-deal-text">
                  <span className="exclusive-deal-label">EXCLUSIVE DEAL:</span> {latestBattery.dealInfo}
                </p>
              )}
              <div className="summary-actions">
                <a 
                  href={latestBattery.productLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="deal-link"
                >
                  Find the Best Deal
                </a>
                <button onClick={handleBack} className="change-btn">
                  <span className="change-btn-text">Change</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Shuttle Card */}
        {latestShuttle && (
          <div className="option-row summary-card">
            <div className="product-image-container">
              <img 
                src={latestShuttle.image} 
                alt={latestShuttle.name}
                className="product-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = latestShuttle.fallbackImage || `https://dummyimage.com/150x100/cccccc/000000&text=Shuttle`;
                }}
              />
            </div>
            <div className="option-details">
              <h3>{latestShuttle.name}</h3>
              <p className="price-tag">MSRP: ${formatPrice(latestShuttle.msrp)}</p>
              <div className="summary-actions">
                <a 
                  href={latestShuttle.productLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="deal-link"
                >
                  Find the Best Deal
                </a>
                <button onClick={handleBack} className="change-btn">
                  <span className="change-btn-text">Change</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pole Card */}
        {latestPole && (
          <div className="option-row summary-card">
            <div className="product-image-container">
              <img 
                src={latestPole.image} 
                alt={latestPole.name}
                className="product-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = latestPole.fallbackImage || `https://dummyimage.com/150x100/cccccc/000000&text=Pole`;
                }}
              />
            </div>
            <div className="option-details">
              <h3>{latestPole.name}</h3>
              <p className="price-tag">MSRP: ${formatPrice(latestPole.msrp)}</p>
              <div className="summary-actions">
                <a 
                  href={latestPole.productLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="deal-link"
                >
                  Find the Best Deal
                </a>
                <button onClick={handleBack} className="change-btn">
                  <span className="change-btn-text">Change</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Primary navigation buttons */}
      <div className="navigation-buttons">
        <button onClick={handleBack} className="nav-btn">
          Previous Step
        </button>
        <button onClick={handleReset} className="nav-btn reset-btn">
          Start Over
        </button>
      </div>
      
      {/* Step indicator / Breadcrumbs */}
      <div className="step-indicator">
        <div className="step-progress">
          <div className="step-label">Jump to:</div>
          <button onClick={() => navigate('/compare-screens')} className="step-btn">1. Screen</button>
          <button onClick={() => navigate('/transducer')} className="step-btn">2. Transducer</button>
          <button onClick={() => navigate('/battery')} className="step-btn">3. Battery</button>
          <button onClick={() => navigate('/shuttle')} className="step-btn">4. Shuttle</button>
          <button onClick={() => navigate('/pole')} className="step-btn">5. Pole</button>
          <button className="step-btn active summary-link">Bundle Summary</button>
        </div>
      </div>

      <FeedbackSection 
        selectedScreen={latestScreen}
        selectedLivescope={latestLivescope}
        selectedOpenWater={latestOpenWater}
        selectedIceFisher={latestIceFisher}
        battery={latestBattery}
        shuttle={latestShuttle}
        pole={latestPole}
        totalMSRP={totalMSRP}
      />
    </div>
  );
};

export default Summary;

